import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Check, Flag, ListChecks, FileText, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader, ProgressBar, Ring } from "@/components/common";
import { NoBizCta } from "@/pages/Journey";
import { LaunchAnswers } from "@/pages/launch/LaunchAnswers";

function FieldRow({ f, bizId, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(f.value);
  const save = async () => {
    try {
      await api.put(`/businesses/${bizId}/answers`, { field: f.field, value: v, lesson_id: f.lesson_id });
      onSaved(f.field, v.trim());
      setEdit(false);
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="py-3" data-testid={`biz-field-${f.field}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold text-[#5C6B73] flex items-center gap-1.5">{f.value ? <Check className="h-3.5 w-3.5 text-[#1DB99A]" /> : <span className="h-1.5 w-1.5 rounded-full bg-[#EADEC9]" />}{f.label}</div>
        {!edit && <button onClick={() => setEdit(true)} className="h-8 w-8 rounded-full hover:bg-[#F5F0E6] flex items-center justify-center" data-testid={`biz-field-edit-${f.field}`}><Pencil className="h-3.5 w-3.5 text-[#8C9BA5]" /></button>}
      </div>
      {edit ? (
        <div className="mt-2">
          <textarea value={v} onChange={(e) => setV(e.target.value)} rows={3} className="w-full rounded-xl border border-[#EADEC9] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1DB99A]" data-testid={`biz-field-input-${f.field}`} />
          <div className="flex gap-2 mt-2"><button onClick={save} className="sb-btn-primary min-h-[40px] text-sm" data-testid={`biz-field-save-${f.field}`}>Save</button><button onClick={() => { setEdit(false); setV(f.value); }} className="sb-btn-ghost min-h-[40px] text-sm">Cancel</button></div>
        </div>
      ) : f.value ? <p className="text-sm mt-1 whitespace-pre-wrap">{f.value}</p>
        : f.lesson_id ? <Link to={`/lesson/${f.lesson_id}`} className="text-xs text-[#179B80] font-semibold mt-1 inline-block">Complete in lesson →</Link> : null}
    </div>
  );
}

function Basics({ biz, onSave }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ name: biz.name, domain: biz.domain || "", category: biz.category, kind: biz.kind, budget: biz.budget, hours_per_week: biz.hours_per_week });
  const rows = [["name", "Business name"], ["domain", "Domain"], ["category", "Business category"], ["kind", "Product or service"], ["budget", "Budget available"], ["hours_per_week", "Hours per week"]];
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-2" data-testid="biz-basics">
      <div className="col-span-2 text-xs text-[#8C9BA5]">Selected idea: <b className="text-[#1E2B3C]">{biz.idea_name}</b></div>
      {rows.map(([k, l]) => (
        <label key={k} className="block"><span className="text-[11px] font-bold text-[#5C6B73]">{l}</span>
          {edit ? <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-[#EADEC9] px-2 text-sm" data-testid={`biz-basic-input-${k}`} />
            : <div className="text-sm font-semibold mt-0.5 truncate">{biz[k] || "—"}</div>}
        </label>
      ))}
      <div className="col-span-2">
        {edit ? <button onClick={async () => { await onSave(f); setEdit(false); }} className="sb-btn-primary min-h-[40px] text-sm" data-testid="biz-basics-save-btn">Save details</button>
          : <button onClick={() => setEdit(true)} className="sb-btn-ghost min-h-[40px] text-sm" data-testid="biz-basics-edit-btn"><Pencil className="h-3.5 w-3.5" />Edit details</button>}
      </div>
    </div>
  );
}

export default function MyBusiness() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState("IDENTITY");
  const load = () => api.get("/workspace").then((r) => setD(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, []);
  if (err) return <NoBizCta />;
  if (!d) return <Loader />;
  const onSaved = (field, value) => setD((x) => ({ ...x, sections: x.sections.map((s) => ({ ...s, fields: s.fields.map((f) => f.field === field ? { ...f, value } : f) })).map((s) => ({ ...s, filled: s.fields.filter((f) => f.value).length })) }));
  const saveBasics = async (f) => { await api.patch(`/businesses/${d.business.id}`, f); toast.success("Business updated"); load(); };
  return (
    <div data-testid="my-business-page">
      <PageHeader eyebrow="My Business" title={d.business.name} subtitle="Everything you decide in lessons is saved here and grows into your business." right={<Ring value={d.overall} size={64} />} />
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          <section className="sb-card p-5"><div className="sb-eyebrow">Business identity</div><Basics biz={d.business} onSave={saveBasics} /></section>
          {d.sections.map((s) => (
            <section key={s.title} className="sb-card" data-testid={`biz-section-${s.title}`}>
              <button onClick={() => setOpen(open === s.title ? "" : s.title)} className="w-full flex items-center gap-3 p-5" data-testid={`biz-section-toggle-${s.title}`}>
                <span className="font-display font-extrabold flex-1 text-left">{s.title}</span>
                <span className="text-xs font-bold text-[#5C6B73]">{s.filled}/{s.fields.length}</span>
                <ProgressBar value={(100 * s.filled) / s.fields.length} className="w-16" />
                <ChevronDown className={`h-4 w-4 transition-transform ${open === s.title ? "rotate-180" : ""}`} />
              </button>
              {open === s.title && <div className="px-5 pb-3 divide-y divide-[#EADEC9] border-t border-[#EADEC9]">{s.fields.map((f) => <FieldRow key={f.field} f={f} bizId={d.business.id} onSaved={onSaved} />)}</div>}
            </section>
          ))}
          <LaunchAnswers />
        </div>
        <div className="space-y-5">
          <section className="sb-card p-5" data-testid="biz-progress-summary">
            <div className="sb-eyebrow mb-3">Progress</div>
            {[["Lessons", d.lessons_done, d.lessons_total], ["Checklist", d.checklist_done, d.checklist_total], ["Exercises", d.exercises_done, d.lessons_total]].map(([l, a, b]) => (
              <div key={l} className="mb-3"><div className="flex justify-between text-xs font-semibold mb-1"><span>{l}</span><span>{a} / {b}</span></div><ProgressBar value={(100 * a) / (b || 1)} /></div>
            ))}
            <Link to="/checklist" className="sb-btn-ghost w-full text-sm mt-2" data-testid="biz-open-checklist-btn"><ListChecks className="h-4 w-4" />Master checklist</Link>
          </section>
          <section className="sb-card p-5" id="milestones" data-testid="biz-milestones">
            <div className="sb-eyebrow mb-4">Your Progress</div>
            <ol className="relative border-l-2 border-[#EADEC9] ml-3 space-y-4">
              {d.milestones.map((m) => (
                <li key={m.key} className="pl-5 relative">
                  <span className={`absolute -left-[11px] top-0.5 h-5 w-5 rounded-full flex items-center justify-center ${m.achieved ? "bg-[#1DB99A] text-white" : "bg-white border-2 border-[#EADEC9]"}`}>{m.achieved && <Flag className="h-2.5 w-2.5" />}</span>
                  <div className={`text-sm font-semibold ${m.achieved ? "" : "text-[#8C9BA5]"}`}>{m.label}</div>
                  {m.achieved_at && <div className="text-[11px] text-[#8C9BA5]">{new Date(m.achieved_at).toLocaleDateString()}</div>}
                </li>
              ))}
            </ol>
          </section>
          <section className="sb-card p-5" data-testid="biz-outputs">
            <div className="sb-eyebrow mb-2">Stage outputs</div>
            {d.stages.map((s) => <Link key={s.key} to={`/stage/${encodeURIComponent(s.key)}/output`} className="flex items-center gap-2 py-2 text-sm font-semibold hover:text-[#179B80]"><FileText className={`h-4 w-4 ${s.complete ? "text-[#1DB99A]" : "text-[#8C9BA5]"}`} />{s.output}<span className="ml-auto text-xs text-[#8C9BA5]">{s.pct}%</span></Link>)}
          </section>
        </div>
      </div>
    </div>
  );
}
